import { fmtPct } from '../../constants/insightsText';
import { insightTone } from '../insights/prepare';

// Crowd at cursor (spec section 3.8): a violin of the forecasters' q50s at the
// hovered quarter-hour, drawn as SVG in uPlot's cursor layer so moving the
// cursor never redraws the canvas.
const NS = 'http://www.w3.org/2000/svg';
const HALF = 34;
const MIN_FORECASTERS = 10; // engine default, until params arrive

// Width profile from 21 quantiles (0, 5, ..., 100%): density between
// neighbours ~ 5% / their distance, lightly smoothed, widest point = half.
export function violinProfile(q, half = HALF) {
  const mids = [];
  const dens = [];
  for (let k = 0; k < q.length - 1; k += 1) {
    mids.push((q[k] + q[k + 1]) / 2);
    dens.push(0.05 / Math.max(q[k + 1] - q[k], 1e-6));
  }
  const smooth = dens.map((d, k) => 0.15 * (dens[k - 1] ?? d) + 0.7 * d + 0.15 * (dens[k + 1] ?? d));
  const max = Math.max(...smooth);
  return [
    { v: q[0], w: 0 },
    ...mids.map((v, k) => ({ v, w: (smooth[k] / max) * half })),
    { v: q[q.length - 1], w: 0 },
  ];
}

export default function crowdLensPlugin({ getState }) {
  let svg = null;
  let path = null;
  let labels = [];

  const clear = () => {
    if (path) path.setAttribute('d', '');
    labels.forEach((l) => l.remove());
    labels = [];
  };

  return {
    hooks: {
      init: [(u) => {
        svg = document.createElementNS(NS, 'svg');
        svg.setAttribute('aria-hidden', 'true');
        Object.assign(svg.style, {
          position: 'absolute', left: '0', top: '0', width: '100%', height: '100%',
          overflow: 'visible', pointerEvents: 'none',
        });
        path = document.createElementNS(NS, 'path');
        svg.appendChild(path);
        u.over.appendChild(svg);
      }],
      setCursor: [(u) => {
        clear();
        const st = getState();
        const idx = u.cursor.idx;
        if (!st || !st.prep || idx == null || (st.types && st.types.crowd === false)) return;
        const sec = u.data[0][idx];
        const i = st.prep.indexOf.get(sec);
        const q = i == null ? null : st.prep.crowdQ[i];
        if (!q || (st.prep.n[i] || 0) < (st.prep.params.min_forecasters ?? MIN_FORECASTERS)) return;
        const x = u.valToPos(sec, 'x');
        const color = insightTone('drop', st.dark);
        const prof = violinProfile(q);
        const pt = ({ v, w }, sign) => `${(x + sign * w).toFixed(1)},${u.valToPos(v, 'y').toFixed(1)}`;
        const right = prof.map((p) => pt(p, 1));
        const left = prof.slice().reverse().map((p) => pt(p, -1));
        path.setAttribute('d', `M${right.join('L')}L${left.join('L')}Z`);
        path.setAttribute('fill', color);
        path.setAttribute('fill-opacity', '0.14');
        path.setAttribute('stroke', color);
        path.setAttribute('stroke-width', '1');
        const sp = st.prep.split[i];
        if (!sp) return;
        [[sp.upper, sp.upper_share], [sp.lower, 1 - sp.upper_share]].forEach(([v, share]) => {
          const t = document.createElementNS(NS, 'text');
          t.setAttribute('x', (x - HALF - 6).toFixed(1));
          t.setAttribute('y', (u.valToPos(v, 'y') + 4).toFixed(1));
          t.setAttribute('text-anchor', 'end');
          t.setAttribute('font-size', '11');
          t.setAttribute('font-weight', '600');
          t.setAttribute('font-family', "'Inter Variable', ui-sans-serif, sans-serif");
          t.setAttribute('fill', insightTone('drop', st.dark, true));
          t.textContent = fmtPct(share);
          svg.appendChild(t);
          labels.push(t);
        });
      }],
      destroy: [() => {
        if (svg) svg.remove();
        svg = null;
      }],
    },
  };
}
